import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Eyebrow, Reveal } from "@/components/Primitives";
import { BUSINESS_TYPES, ILLUSTRATIVE_RATE, money } from "@/lib/site";

const Num = ({ value, testid, className }) => (
  <span className="relative inline-block overflow-hidden align-bottom">
    <AnimatePresence initial={false}>
      <motion.span
        key={value}
        initial={{ y: "60%", opacity: 0 }}
        animate={{ y: "0%", opacity: 1 }}
        exit={{ y: "-60%", opacity: 0, position: "absolute" }}
        transition={{ duration: 0.35 }}
        className={`block ${className}`}
        data-testid={testid}
      >
        {value}
      </motion.span>
    </AnimatePresence>
  </span>
);

const Field = ({ label, value, children }) => (
  <div>
    <div className="flex items-baseline justify-between">
      <span className="text-sm font-medium text-slate-600">{label}</span>
      <span className="font-mono text-lg font-semibold">{value}</span>
    </div>
    <div className="mt-4">{children}</div>
  </div>
);

export const Calculator = ({ onLock }) => {
  const [volume, setVolume] = useState(40000);
  const [rate, setRate] = useState(3.2);
  const [type, setType] = useState(BUSINESS_TYPES[0]);
  const monthly = Math.max(0, (volume * (rate - ILLUSTRATIVE_RATE)) / 100);
  return (
    <section id="calculator" className="py-24 lg:py-36" data-testid="calculator-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <Reveal className="lg:col-span-5">
          <Eyebrow>03 — Savings calculator</Eyebrow>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.02]">
            What are your fees <span className="font-serif-i text-[var(--cobalt)]">really</span> costing you?
          </h2>
          <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">
            Slide in your monthly card sales and your current effective rate (total fees ÷ total card sales on your
            statement). We'll show a ballpark of what you could keep each year.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {BUSINESS_TYPES.slice(0, 4).map((t) => (
              <button
                key={t}
                onClick={() => setType(t)}
                data-testid={`calc-type-${t.split(" ")[0].toLowerCase()}`}
                className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${
                  type === t ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-slate-300 hover:border-[var(--ink)]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <div className="rounded-[32px] bg-white border border-slate-200 shadow-[0_40px_80px_-40px_rgba(11,18,32,0.35)] overflow-hidden">
            <div className="p-7 sm:p-10 space-y-10">
              <Field label="Monthly card volume" value={money(volume)}>
                <Slider data-testid="calc-volume-slider" min={5000} max={500000} step={1000} value={[volume]} onValueChange={(v) => setVolume(v[0])} />
              </Field>
              <Field label="Current effective rate" value={`${rate.toFixed(2)}%`}>
                <Slider data-testid="calc-rate-slider" min={1.5} max={5} step={0.05} value={[rate]} onValueChange={(v) => setRate(v[0])} />
              </Field>
            </div>
            <div className="bg-[var(--ink)] text-white p-7 sm:p-10 grid sm:grid-cols-2 gap-8">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-300">Est. monthly savings</p>
                <p className="mt-2 font-display text-4xl sm:text-5xl font-semibold">
                  <Num value={money(monthly)} testid="calc-monthly-savings" />
                </p>
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-300">Est. annual savings</p>
                <p className="mt-2 font-display text-4xl sm:text-5xl font-semibold text-[var(--mint)]">
                  <Num value={money(monthly * 12)} testid="calc-annual-savings" />
                </p>
              </div>
              <p className="sm:col-span-2 text-xs text-slate-300 leading-relaxed">
                Illustrative estimate comparing your rate with a {ILLUSTRATIVE_RATE.toFixed(2)}% effective rate. Your actual
                pricing depends on your business type, card mix and volume, and is confirmed after a free statement review.
              </p>
              <button
                onClick={() => onLock({ monthly_volume: volume, current_rate: rate, business_type: type })}
                data-testid="calc-lock-cta"
                className="sm:col-span-2 group inline-flex items-center justify-center gap-3 rounded-full bg-[var(--cobalt)] px-7 py-4 font-semibold hover:bg-white hover:text-[var(--ink)] transition-colors"
              >
                Get my exact quote
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
